/**
 * @author Tanmay <tanmay.pawar@314ecorp.com>
 * @description Keycloak Provider
 */

import React, { PropsWithChildren, useEffect, useState } from 'react';
import Keycloak from 'keycloak-js';
import log from 'loglevel';

import Client from './Client';
import KeycloakContext from './KeycloakContext';

interface KeycloakProps {
	client: Client;
	loginPage: JSX.Element;
	onReady?: Keycloak['onReady'];
	onSuccess?: (keycloak: Keycloak) => void;
	onError?: Keycloak['onAuthError'];
	onTokenExpired?: Keycloak['onTokenExpired'];
}

const Provider: React.FC<PropsWithChildren<KeycloakProps>> = (props) => {
	const { children, client } = props;
	const keycloak = client.keycloak;
	const [value, setValue] = useState({ isAuthenticated: false, keycloak, initOptions: client.initOptions });
	const [showLogin, setShowLogin] = useState(false);

	useEffect(() => {
		keycloak.onReady = (authenticated?: boolean | undefined) => {
			if (!authenticated) {
				setShowLogin(true);
			}
			props.onReady?.(authenticated);
		};

		keycloak.onAuthSuccess = () => {
			props.onSuccess?.(keycloak);
			setValue({ ...value, isAuthenticated: true });
		};

		keycloak.onAuthError = (errorData: Keycloak.KeycloakError) => {
			props.onError?.(errorData);
		};

		keycloak.onTokenExpired = () => {
			keycloak
				.updateToken(5)
				.then(() => {
					props.onTokenExpired?.();
				})
				.catch((ex) => {
					keycloak.clearToken();
					log.error(ex);
				});
		};

		keycloak.init(client.initOptions).catch((error) => {
			log.error(error);
		});
	}, []);

	if (showLogin) {
		const loginProps = {
			keycloak: client.keycloak,
			initOptions: {
				...client.initOptions,
				onLoad: 'login-required',
			},
		};

		if (props.loginPage) {
			return React.cloneElement(props.loginPage, loginProps);
		}
	}

	return (
		<KeycloakContext.Provider value={value}>
			{value.isAuthenticated ? children : <span>Authenticating...</span>}
		</KeycloakContext.Provider>
	);
};

export default Provider;
